import { Container } from "aurelia-dependency-injection";
import { defineComponent, PropType } from "vue";
import { ToastrService } from "../../../../../../services/toastr.service";
import { Hospitalization } from "../../../../../../models/pal-health-history/hospitalization";
import { IEnumResponse } from "../../../../../../interfaces/i-enum";
import { IGetPatientsSlimInfoResult } from "../../../../../../resources-vue/vue-interfaces/i-patient";
import { PatientService } from "../../../../../../services/patient.service";
import { formatDate } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { ApplicationEnum } from "../../../../../../enums/ApplicationEnum";
import { IHospitalization } from "../../../../../../resources-vue/vue-interfaces/i-health";

import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import HospitalAdmissionForm from "../HospitalAdmissionForm/HospitalAdmissionForm.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        TablePrompt,
        HospitalAdmissionForm,
        EmptyTextHandler
    },

    props: {
        hospitalization: {
            type: Object as PropType<IHospitalization>
        },
        columns: {
            type: Number,
            default: 6
        },
        index: {
            type: Number,
            default: 0
        },
        pageReset: {
            type: Function
        },
        patientId: {
            type: String,
            default: ""
        },
        providerId: {
            type: String,
            default: ""
        },
        patientInfo: {
            type: Object as PropType<IGetPatientsSlimInfoResult>,
            default: null
        },
        roles: {
            type: Array as PropType<IEnumResponse[]>,
            default: []
        },
        accountId: {
            type: String
        },
        application: {
            type: Number
        },
        handleCancelEditHospitalization: {
            type: Function
        }
    },

    data() {
        return {
            _toastrService: null as ToastrService,
            _patientsService: null as PatientService,
            isEditRequested: false as boolean,
            isDeleteRequested: false as boolean,
            isLoading: false as boolean,
            isDeleteInProgress: false as boolean,
            isViewDetailsRequested: false as boolean,
            cachedData: null,
            formatDate
        };
    },

    computed: {
        diagnosesList() {
            return this.hospitalization?.HospitalizationHistoryDiagnoses.map(
                (diagnosis) => `${diagnosis.Code} - ${diagnosis.Description}`
            ).join(", ");
        }
    },

    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._patientsService = Container.instance.get(PatientService);
    },

    methods: {
        openEditHospitalization() {
            this.cachedData = new Hospitalization(this.hospitalization);
            this.isEditRequested = true;
        },

        openHospitalizationView() {
            this.cachedData = new Hospitalization(this.hospitalization);
            this.isViewDetailsRequested = true;
        },

        async editHospitalization() {
            try {
                this.isLoading = true;
                const payload = {
                    Application: ApplicationEnum.AxxessPalliative,
                    AccountId: this.accountId,
                    PalliativePatientHospitalizationHistories: [this.cachedData]
                };
                await this._patientsService.updateHospitalization(this.patientId, payload);
                this.isEditRequested = false;
                await this.pageReset();
                this._toastrService.success({
                    title: "Hospitalization Updated",
                    message: "Hospitalization has been successfully updated."
                });
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Error - Hospitalizations",
                    message: "There was an error updating Hospitalization. Please try again."
                });
            } finally {
                this.isLoading = false;
            }
        },

        closeEditHospitalization() {
            this.isEditRequested = false;
            this.handleCancelEditHospitalization(this.index, this.cachedData);
        },

        closeViewDetailsWindow() {
            this.isViewDetailsRequested = false;
        },

        openDeleteHospitalization() {
            this.isDeleteRequested = true;
        },

        async deleteHospitalization() {
            try {
                this.isDeleteInProgress = true;
                const query = {
                    application: ApplicationEnum.AxxessPalliative,
                    accountId: this.accountId,
                    hospitalizationId: this.hospitalization.Id
                };
                await this._patientsService.deleteHospitalization(this.patientId, query);
                await this.pageReset();
                this.isDeleteRequested = false;
                this._toastrService.success({
                    title: "Hospitalization Deleted",
                    message: "Hospitalization has been successfully deleted."
                });
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Error - Hospitalizations",
                    message: "There was an error deleting Hospitalization. Please try again."
                });
            } finally {
                this.isDeleteInProgress = false;
            }
        },

        closeDeleteHospitalization() {
            this.isDeleteRequested = false;
        }
    }
});
